<template>
  <v-dialog v-model="show" max-width="1200" persistent :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-card-title class="text-h6">Kind anzeigen</v-card-title>
      <v-card-text>
        <!-- Navigation Alert -->
        <v-alert type="info" class="mb-4" dense>
          <v-row align="center">
            <v-col class="grow">
              Die Daten werden vom verknüpften Klienten übernommen. Änderungen sind nur dort möglich.
            </v-col>
            <v-col class="shrink">
              <v-btn small outlined @click="navigateToChild">
                Zum Klienten navigieren
                <v-icon right>mdi-open-in-new</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>

        <!-- Rich Data Display -->
        <v-list dense outlined>
          <v-list-item>
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-account</v-icon>
            </v-list-item-icon>
            <v-list-item-content
              @mouseenter="setCopyIconHover('name', true)"
              @mouseleave="setCopyIconHover('name', false)"
            >
              <v-list-item-title class="d-flex align-center">
                <span class="font-weight-bold mr-2">Name:</span>
                <span>{{ fullName }}</span>
                <v-btn
                  v-if="fullName && isClipboardSupported"
                  icon
                  small
                  class="ml-2"
                  :style="{ 
                    opacity: copyIconHover.name ? 1 : 0,
                    transition: 'opacity 0.2s ease'
                  }"
                  :color="copySuccessState.name ? 'green' : $store.state.theme.primary"
                  @click="copyToClipboard(fullName, 'name')"
                  @mouseenter="setCopyIconHover('name', true)"
                  @mouseleave="setCopyIconHover('name', false)"
                >
                  <v-icon small>
                    {{ copySuccessState.name ? 'mdi-check' : 'mdi-content-copy' }}
                  </v-icon>
                </v-btn>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-map-marker</v-icon>
            </v-list-item-icon>
            <v-list-item-content
              @mouseenter="setCopyIconHover('address', true)"
              @mouseleave="setCopyIconHover('address', false)"
            >
              <v-list-item-title class="d-flex align-center">
                <span class="font-weight-bold mr-2">Adresse:</span>
                <span>{{ fullAddress }}</span>
                <v-btn
                  v-if="fullAddress && isClipboardSupported"
                  icon
                  small
                  class="ml-2"
                  :style="{ 
                    opacity: copyIconHover.address ? 1 : 0,
                    transition: 'opacity 0.2s ease'
                  }"
                  :color="copySuccessState.address ? 'green' : $store.state.theme.primary"
                  @click="copyToClipboard(fullAddress, 'address')"
                  @mouseenter="setCopyIconHover('address', true)"
                  @mouseleave="setCopyIconHover('address', false)"
                >
                  <v-icon small>
                    {{ copySuccessState.address ? 'mdi-check' : 'mdi-content-copy' }}
                  </v-icon>
                </v-btn>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-email</v-icon>
            </v-list-item-icon>
            <v-list-item-content
              @mouseenter="setCopyIconHover('email', true)"
              @mouseleave="setCopyIconHover('email', false)"
            >
              <v-list-item-title class="d-flex align-center">
                <span class="font-weight-bold mr-2">E-Mail:</span>
                <span>{{ (childData && childData.email) || '' }}</span>
                <v-btn
                  v-if="(childData && childData.email) && isClipboardSupported"
                  icon
                  small
                  class="ml-2"
                  :style="{ 
                    opacity: copyIconHover.email ? 1 : 0,
                    transition: 'opacity 0.2s ease'
                  }"
                  :color="copySuccessState.email ? 'green' : $store.state.theme.primary"
                  @click="copyToClipboard(childData.email, 'email')"
                  @mouseenter="setCopyIconHover('email', true)"
                  @mouseleave="setCopyIconHover('email', false)"
                >
                  <v-icon small>
                    {{ copySuccessState.email ? 'mdi-check' : 'mdi-content-copy' }}
                  </v-icon>
                </v-btn>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-phone</v-icon>
            </v-list-item-icon>
            <v-list-item-content
              @mouseenter="setCopyIconHover('phone', true)"
              @mouseleave="setCopyIconHover('phone', false)"
            >
              <v-list-item-title class="d-flex align-center">
                <span class="font-weight-bold mr-2">Telefon:</span>
                <span>{{ (childData && childData.telefon) || '' }}</span>
                <v-btn
                  v-if="(childData && childData.telefon) && isClipboardSupported"
                  icon
                  small
                  class="ml-2"
                  :style="{ 
                    opacity: copyIconHover.phone ? 1 : 0,
                    transition: 'opacity 0.2s ease'
                  }"
                  :color="copySuccessState.phone ? 'green' : $store.state.theme.primary"
                  @click="copyToClipboard(childData.telefon, 'phone')"
                  @mouseenter="setCopyIconHover('phone', true)"
                  @mouseleave="setCopyIconHover('phone', false)"
                >
                  <v-icon small>
                    {{ copySuccessState.phone ? 'mdi-check' : 'mdi-content-copy' }}
                  </v-icon>
                </v-btn>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-calendar</v-icon>
            </v-list-item-icon>
            <v-list-item-content
              @mouseenter="setCopyIconHover('birthdate', true)"
              @mouseleave="setCopyIconHover('birthdate', false)"
            >
              <v-list-item-title class="d-flex align-center">
                <span class="font-weight-bold mr-2">Geburtsdatum:</span>
                <span>{{ (childData && childData.geburtsdatum) || '' }}</span>
                <v-btn
                  v-if="(childData && childData.geburtsdatum) && isClipboardSupported"
                  icon
                  small
                  class="ml-2"
                  :style="{ 
                    opacity: copyIconHover.birthdate ? 1 : 0,
                    transition: 'opacity 0.2s ease'
                  }"
                  :color="copySuccessState.birthdate ? 'green' : $store.state.theme.primary"
                  @click="copyToClipboard(childData.geburtsdatum, 'birthdate')"
                  @mouseenter="setCopyIconHover('birthdate', true)"
                  @mouseleave="setCopyIconHover('birthdate', false)"
                >
                  <v-icon small>
                    {{ copySuccessState.birthdate ? 'mdi-check' : 'mdi-content-copy' }}
                  </v-icon>
                </v-btn>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-card-account-details</v-icon>
            </v-list-item-icon>
            <v-list-item-content
              @mouseenter="setCopyIconHover('svnr', true)"
              @mouseleave="setCopyIconHover('svnr', false)"
            >
              <v-list-item-title class="d-flex align-center">
                <span class="font-weight-bold mr-2">SV-Nr:</span>
                <span>{{ (childData && childData.svnr) || '' }}</span>
                <span v-if="childData && childData.versicherungsträger" class="ml-2">({{ insurerName }})</span>
                <v-btn
                  v-if="(childData && childData.svnr) && isClipboardSupported"
                  icon
                  small
                  class="ml-2"
                  :style="{ 
                    opacity: copyIconHover.svnr ? 1 : 0,
                    transition: 'opacity 0.2s ease'
                  }"
                  :color="copySuccessState.svnr ? 'green' : $store.state.theme.primary"
                  @click="copyToClipboard(childData.svnr, 'svnr')"
                  @mouseenter="setCopyIconHover('svnr', true)"
                  @mouseleave="setCopyIconHover('svnr', false)"
                >
                  <v-icon small>
                    {{ copySuccessState.svnr ? 'mdi-check' : 'mdi-content-copy' }}
                  </v-icon>
                </v-btn>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="px-6 pb-5">
        <v-spacer></v-spacer>
        <v-btn :color="$store.state.theme.primary" text @click="close">
          Schließen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ClipboardMixin from '../mixins/ClipboardMixin.js';

export default {
  name: 'ChildViewDialog',
  mixins: [ClipboardMixin],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    childData: {
      type: Object,
      default: null
    },
    insurer: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    fullName() {
      if (!this.childData) return '';
      return [
        this.childData.titel_vorgestellt,
        this.childData.vorname,
        this.childData.nachname,
        this.childData.titel_nachgestellt
      ].filter(Boolean).join(' ').trim();
    },
    fullAddress() {
      if (!this.childData) return '';
      const parts = [
        this.childData.adresse,
        [this.childData.plz, this.childData.ort].filter(Boolean).join(' ')
      ].filter(Boolean);
      return parts.join(', ');
    },
    insurerName() {
      if (!this.childData || !this.childData.versicherungsträger || !this.insurer) return '';
      const insurerItem = this.insurer?.find(i => i.id === this.childData.versicherungsträger);
      return insurerItem ? insurerItem.name : '';
    }
  },
  methods: {
    close() {
      this.$emit('close');
      this.$emit('input', false);
    },
    navigateToChild() {
      if (this.childData && this.childData.id) {
        this.$router.push({ path: `/klienten/${this.childData.id}` });
        this.close();
      }
    }
  }
};
</script>

<style scoped>
</style>